import { useEffect, useRef, useState } from "react";
import { ChatTeardropText, X, PaperPlaneRight, GraduationCap } from "@phosphor-icons/react";
import { api } from "@/lib/api";

function sessionId() {
  let id = localStorage.getItem("gor_chat_session");
  if (!id) {
    id = `gor-${crypto.randomUUID()}`;
    localStorage.setItem("gor_chat_session", id);
  }
  return id;
}

const SUGGESTIONS = [
  "What's the difference between PCT and Paris route?",
  "How do I draft a strong independent claim?",
  "When should I value patents using DCF vs market approach?",
];

/* Claude is instructed to reply in plain prose; this is a safety net that
   strips heading markers and renders **bold** if it ever slips through. */
function renderAssistantText(text) {
  const cleaned = text.replace(/^#{1,6}\s*/gm, "").replace(/^\s*[-*]\s+/gm, "· ");
  return cleaned.split(/(\*\*[^*]+\*\*)/g).map((part, i) =>
    part.startsWith("**") && part.endsWith("**") ? (
      <strong key={i} className="font-semibold text-[#F5F4F0]">
        {part.slice(2, -2)}
      </strong>
    ) : (
      part
    )
  );
}

export default function ChatWidget() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([
    {
      role: "assistant",
      content:
        "Namaste — I'm Ramana, your AI tutor. Ask me anything about patents, trademarks or IP strategy.",
    },
  ]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const scrollerRef = useRef(null);

  useEffect(() => {
    scrollerRef.current?.scrollTo({ top: 9e9, behavior: "smooth" });
  }, [messages, open, loading]);

  async function send(text) {
    const msg = (text ?? input).trim();
    if (!msg || loading) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", content: msg }]);
    setLoading(true);
    try {
      const { data } = await api.post("/chat/send", {
        session_id: sessionId(),
        message: msg,
      });
      setMessages((m) => [...m, { role: "assistant", content: data.reply }]);
    } catch (e) {
      setMessages((m) => [
        ...m,
        {
          role: "assistant",
          content:
            "I couldn't reach the tutor service. Please try again in a moment.",
        },
      ]);
    } finally {
      setLoading(false);
    }
  }

  return (
    <>
      <button
        data-testid="chat-toggle-button"
        onClick={() => setOpen((v) => !v)}
        className="fixed bottom-6 right-6 z-30 group"
        aria-label="Open AI tutor"
      >
        <span className="absolute inset-0 rounded-full bg-[#C6A87C] opacity-25 animate-ping group-hover:hidden" />
        <span className="relative flex items-center gap-2 h-14 px-5 rounded-full border border-[#C6A87C]/60 bg-[#061324]/75 backdrop-blur-md text-[#C6A87C] shadow-[0_15px_40px_-12px_rgba(6,19,36,0.8)] transition-colors duration-300 hover:bg-[#C6A87C] hover:text-[#061324]">
          {open ? <X size={20} /> : <ChatTeardropText size={22} weight="fill" />}
          <span className="text-sm tracking-wide hidden sm:inline">
            {open ? "Close" : "Ask Ramana"}
          </span>
        </span>
      </button>

      {open && (
        <div
          data-testid="chat-window"
          className="fixed bottom-24 right-6 z-30 w-[92vw] max-w-[400px] h-[560px] bg-navy border border-[#C6A87C]/25 shadow-[0_30px_80px_-20px_rgba(6,19,36,0.9)] flex flex-col overflow-hidden"
        >
          <div className="bg-abyss text-[#F5F4F0] px-5 py-4 flex items-center gap-3 border-b border-[#C6A87C]/15">
            <span className="w-9 h-9 border border-[#C6A87C]/60 text-[#C6A87C] flex items-center justify-center">
              <GraduationCap weight="fill" size={17} />
            </span>
            <div>
              <div className="font-serif text-lg leading-none">Ramana</div>
              <div className="text-[10px] uppercase tracking-[0.22em] text-[#C6A87C] mt-1">
                AI Tutor · Claude Sonnet
              </div>
            </div>
          </div>

          <div
            ref={scrollerRef}
            data-testid="chat-messages"
            data-lenis-prevent
            className="flex-1 overflow-y-auto px-5 py-4 space-y-4 bg-navy-deep"
          >
            {messages.map((m, i) => (
              <div
                key={i}
                data-testid={`chat-msg-${m.role}-${i}`}
                className={`text-sm leading-relaxed ${
                  m.role === "user" ? "text-right" : "text-left"
                }`}
              >
                <span
                  className={`inline-block px-4 py-2.5 max-w-[85%] whitespace-pre-wrap ${
                    m.role === "user"
                      ? "bg-[#C6A87C] text-[#061324]"
                      : "bg-navy border border-[#C6A87C]/15 text-[#F5F4F0]/90"
                  }`}
                >
                  {m.role === "assistant" ? renderAssistantText(m.content) : m.content}
                </span>
              </div>
            ))}
            {loading && (
              <div data-testid="chat-loading" className="flex gap-1.5 px-2">
                <span className="w-1.5 h-1.5 bg-[#C6A87C] rounded-full animate-bounce" />
                <span className="w-1.5 h-1.5 bg-[#C6A87C] rounded-full animate-bounce [animation-delay:0.15s]" />
                <span className="w-1.5 h-1.5 bg-[#C6A87C] rounded-full animate-bounce [animation-delay:0.3s]" />
              </div>
            )}

            {messages.length <= 1 && !loading && (
              <div className="pt-2 space-y-2">
                <div className="label-overline">Try asking</div>
                {SUGGESTIONS.map((s, i) => (
                  <button
                    key={i}
                    data-testid={`chat-suggestion-${i}`}
                    onClick={() => send(s)}
                    className="block w-full text-left text-sm text-[#F5F4F0]/80 border border-[#C6A87C]/20 hover:border-[#C6A87C]/70 hover:text-[#C6A87C] bg-transparent px-3 py-2 transition-colors duration-300"
                  >
                    {s}
                  </button>
                ))}
              </div>
            )}
          </div>

          <form
            onSubmit={(e) => {
              e.preventDefault();
              send();
            }}
            className="border-t border-[#C6A87C]/15 p-3 flex items-center gap-2 bg-abyss"
          >
            <input
              data-testid="chat-input"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Ask about claims, valuation, licensing..."
              className="flex-1 px-3 h-11 text-sm bg-transparent border border-[#C6A87C]/25 text-[#F5F4F0] placeholder:text-[#8A99AA]/50 focus:border-[#C6A87C] outline-none transition-colors duration-300"
            />
            <button
              data-testid="chat-send-button"
              type="submit"
              disabled={loading || !input.trim()}
              className="h-11 px-4 bg-[#C6A87C] text-[#061324] hover:bg-[#E2C69D] transition-colors duration-300 disabled:opacity-40"
              aria-label="Send message"
            >
              <PaperPlaneRight size={16} weight="fill" />
            </button>
          </form>
        </div>
      )}
    </>
  );
}
